import { closeAllForms } from '../utils/closeallforms';
import { populateAmountUnitsDropdown } from '../utils/populateunits';
import { populateRateSetDropdown } from '../utils/populateratesetdropdown';
import { openForm } from '../utils/openform';
import { updateReferrerFields } from '../utils/updatereferrerfields';
import { getCSRFToken } from '../utils/getCSRFToken';
import { postJSONWithCSRFRetry } from '../utils/fetchWithCSRFRetry';
import { safeJsonParse } from '../utils/authErrorHandler';
import {
  formatDateISO,
  calculateDaysInclusive,
  addDays,
  formatDateDDMMYYYY,
  parseDate,
  getDateRange,
} from '../utils/dateUtils';
import { deepEqual } from '../utils/deepEqual';

export default function registerAddBookingForm() {
  // ADD NEW BOOKING (calendar date click)
  window.Alpine.store('addBookingForm', {
    dateClicked: '',
    startDate: '',
    endDate: '',
    fixedDateRange: '',
    showFixedDateRange: false,
    unavailableDates: [], // format should be ['23-01-2024']
    calendarId: null,
    product: 'Accommodation',
    dateSelectType: 'range',
    productId: null,
    soldAs: null,
    soldAsType: null,
    soldAsData: null,
    unitId: null,
    tourDepartureId: null,
    tourDepartureTime: '',
    departureOptions: [],
    departureRequestId: 0,
    componentDepartureGroups: [],
    selectedComponentDepartures: {},
    componentDepartureRequestId: 0,
    units: 1,
    pinnedUnitIds: [],
    pinnedUnitCount: null,
    prefilledStart: '',
    prefilledEnd: '',
    selectionGate: false,
    selectionGateMessage:
      'This listing isn’t available for the selected dates. Choose another listing, or pick dates it can cover.',
    listingCoverage: null,
    totalUnits: 1,
    isPackage: false, // Track if selected rate set is a package

    // People options
    peopleAsOption: false,
    peoplePerUnit: 0, // Store the people per unit from RateSet
    amountPeopleOptions: [], // Store the generated options for amount_people
    selectedPeople: null, // Default selected option

    // Person type options (Adults/Children)
    usePersonTypes: false,
    adultOptions: [],
    childrenOptions: [],
    selectedAdults: 1,
    selectedChildren: 0,
    maxPersons: 0,

    // Tour duration selection
    tourDuration: 'single', // Default to single day for tours
    showTourDurationToggle: false, // Only show when rate set allows range

    price: 0,
    priceUpdated: false,
    priceLoading: false, // True when price is being calculated
    // Discount information
    discountAmount: 0,
    appliedDiscounts: [],
    flatpickrInstances: {},
    categorizedDates: {
      start: [],
      single: [],
      middle: [],
      end: [],
    },
    minMaxDays: {}, // Min/max days constraints per date
    closedToDays: {}, // Closed-to-arrival/departure flags from backend
    packageClosedToStartDates: [], // Package-only blocked start dates (admin-safe)
    packages: [], // Package info for dynamic availability checking

    // Validation state
    validationErrors: {
      peopleUnits: '',
      startEndDate: '',
    },
    validationTimer: null,

    // Submit message state
    submitMessage: '',
    messageType: '',
    submitMessageTimer: null,

    // Submission state - prevents double submissions
    isSubmitting: false,
    showGuestEmailReuseModal: false,
    guestEmailReuse: {
      existing_guest_name: '',
      booking_count: 0,
    },
    pendingSubmitAction: '',
    pendingFormId: '',
    guestEmailAction: '',
    isInitializing: false, // Backdrop visible while hydrating form data
    showLoadingIndicator: false, // Delayed spinner/text on top of backdrop
    loadingIndicatorShownAt: null,
    loadingIndicatorShowTimer: null,
    loadingIndicatorHideTimer: null,
    initializationTimeoutTimer: null,

    clearInitializationTimers() {
      if (this.loadingIndicatorShowTimer) {
        clearTimeout(this.loadingIndicatorShowTimer);
        this.loadingIndicatorShowTimer = null;
      }
      if (this.loadingIndicatorHideTimer) {
        clearTimeout(this.loadingIndicatorHideTimer);
        this.loadingIndicatorHideTimer = null;
      }
      if (this.initializationTimeoutTimer) {
        clearTimeout(this.initializationTimeoutTimer);
        this.initializationTimeoutTimer = null;
      }
    },

    hideInitializingState() {
      this.isInitializing = false;
      this.showLoadingIndicator = false;
      this.loadingIndicatorShownAt = null;
    },

    startInitializing() {
      this.clearInitializationTimers();
      this.isInitializing = true;
      this.showLoadingIndicator = false;
      this.loadingIndicatorShownAt = null;
      this.loadingIndicatorShowTimer = setTimeout(() => {
        this.showLoadingIndicator = true;
        this.loadingIndicatorShownAt = Date.now();
        this.loadingIndicatorShowTimer = null;
      }, 1000);
      this.initializationTimeoutTimer = setTimeout(() => {
        console.warn('Add booking form initialization timed out');
        this.stopInitializing();
        const toastStore = Alpine.store('toastStore');
        if (toastStore && toastStore.addToast) {
          toastStore.addToast(
            'Loading booking form took too long. Please try again.',
            'warning',
          );
        }
      }, 15000);
    },
    stopInitializing() {
      if (this.loadingIndicatorShowTimer) {
        clearTimeout(this.loadingIndicatorShowTimer);
        this.loadingIndicatorShowTimer = null;
      }
      if (this.initializationTimeoutTimer) {
        clearTimeout(this.initializationTimeoutTimer);
        this.initializationTimeoutTimer = null;
      }

      if (!this.showLoadingIndicator) {
        if (this.loadingIndicatorHideTimer) {
          clearTimeout(this.loadingIndicatorHideTimer);
          this.loadingIndicatorHideTimer = null;
        }
        this.hideInitializingState();
        return;
      }

      const elapsed = Date.now() - (this.loadingIndicatorShownAt || 0);
      const remainingVisibleMs = Math.max(0, 1000 - elapsed);
      if (remainingVisibleMs === 0) {
        this.hideInitializingState();
        return;
      }

      if (this.loadingIndicatorHideTimer) {
        clearTimeout(this.loadingIndicatorHideTimer);
      }
      this.loadingIndicatorHideTimer = setTimeout(() => {
        this.hideInitializingState();
        this.loadingIndicatorHideTimer = null;
      }, remainingVisibleMs);
    },
    hideInitializingOverlayImmediate() {
      this.clearInitializationTimers();
      this.hideInitializingState();
    },

    // Custom fields state
    selectedRateSet: null, // Store the full rate set object including active_custom_fields
    customFields: {}, // Track per-booking-item custom field values
    // Use an object instead of array for customFieldsPerPerson to avoid undefined access issues
    // Keys are person indices (0, 1, 2, etc.), values are objects with field values
    customFieldsPerPerson: {},

    // Safe getter for per-person custom fields - ALWAYS returns an object, never undefined
    // This prevents "Cannot read properties of undefined" errors during Alpine's reactive re-rendering
    getPersonFieldsObj(personIndex) {
      if (!this.customFieldsPerPerson[personIndex]) {
        this.customFieldsPerPerson[personIndex] = {};
      }
      return this.customFieldsPerPerson[personIndex];
    },
    getPersonLabel(count = 2, lowercase = false) {
      const singular = (
        this.selectedRateSet?.person_label_singular || 'Person'
      ).trim();
      const plural = (
        this.selectedRateSet?.person_label_plural || 'People'
      ).trim();
      const label = Number(count) === 1 ? singular : plural;
      return lowercase ? label.toLowerCase() : label;
    },
    getPersonFieldLabel() {
      return this.getPersonLabel(2);
    },

    // Method to validate people vs units
    validatePeopleUnits() {
      console.log('FUNCTION CALLED: validatePeopleUnits');

      // Skip validation if people are not being selected separately from units
      if (!this.peopleAsOption) {
        console.log('Skipping validation - peopleAsOption is false');
        this.clearAllValidationErrors();
        return true;
      }

      let totalPeople = 0;

      if (this.usePersonTypes) {
        totalPeople =
          (parseInt(this.selectedAdults) || 0) +
          (parseInt(this.selectedChildren) || 0);
      } else {
        totalPeople = parseInt(this.selectedPeople) || 0;
      }

      console.log(
        'Validating - totalPeople:',
        totalPeople,
        'units:',
        this.units,
        'peopleAsOption:',
        this.peopleAsOption,
      );

      if (totalPeople < this.units) {
        // Get unit type name
        const unitTypeName = this.soldAsType || 'unit';
        const message = `Please select at least ${
          this.units
        } ${this.getPersonLabel(
          this.units,
          true,
        )} (minimum 1 ${this.getPersonLabel(1, true)} per ${unitTypeName}).`;

        // Use general validation error method with auto-dismiss
        this.setValidationError('peopleUnits', message);
        return false;
      } else {
        // Clear all validation errors immediately when validation passes
        this.clearAllValidationErrors();
        return true;
      }
    },
    // Hides the units field if there is only 1 unit available
    showUnitsField() {
      console.log(
        'showUnitsField called, units:',
        this.totalUnits,
        typeof this.totalUnits,
      );
      return Number(this.totalUnits) > 1;
    },

    // Method to set validation error with auto-dismiss
    setValidationError(field, message) {
      console.log(`❌ Setting validation error for ${field}:`, message);

      // Clear any existing timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
        this.validationTimer = null;
      }

      // Set the error
      this.validationErrors = {
        ...this.validationErrors,
        [field]: message,
      };
      // No timer: all warnings persist until validation passes
    },

    // Method to clear all validation errors
    clearAllValidationErrors() {
      console.log('🧹 Clearing all validation errors');

      // Clear timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
        this.validationTimer = null;
      }

      // Clear all errors by creating new object (forces Alpine reactivity)
      this.validationErrors = {
        peopleUnits: '',
        // Add other validation fields here as needed
      };

      console.log('✅ All validation errors cleared:', this.validationErrors);
    },

    // Method to set submit message with auto-dismiss
    setSubmitMessage(message, type = 'error') {
      console.log(`❌ Setting submit message (${type}):`, message);

      // Clear any existing timer
      if (this.submitMessageTimer) {
        clearTimeout(this.submitMessageTimer);
      }

      // Set the message
      this.submitMessage = message;
      this.messageType = type;

      // Auto-clear error messages after 4 seconds (but keep success messages longer)
      if (type === 'error') {
        this.submitMessageTimer = setTimeout(() => {
          console.log('🕐 Timer fired - clearing submit message');
          this.clearSubmitMessage();
        }, 4000);
      }
    },

    // Method to clear submit message
    clearSubmitMessage() {
      console.log('🧹 Clearing submit message');

      // Clear timer
      if (this.submitMessageTimer) {
        clearTimeout(this.submitMessageTimer);
        this.submitMessageTimer = null;
      }

      // Clear message
      this.submitMessage = '';
      this.messageType = '';
    },

    // Method to check if form is valid for submission
    isFormValid() {
      return this.validatePeopleUnits() && this.startDate && this.endDate;
    },

    // Method to validate email format
    validateEmail(email) {
      // RFC 5322 compliant email regex (simplified but comprehensive)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    // When available date on calendar gets clicked, this opens the Add Booking Form and loads all relevant info
    clearDateSelectionPrefill() {
      this.pinnedUnitIds = [];
      this.pinnedUnitCount = null;
      this.prefilledStart = '';
      this.prefilledEnd = '';
      this.selectionGate = false;
      this.listingCoverage = null;
      this.removeListingPrompt();
      this.syncSelectionGateClass();
    },
    syncSelectionGateClass() {
      const openId =
        this.product === 'Tour' ? 'addTourForm' : 'addAccommodationForm';
      ['addAccommodationForm', 'addTourForm'].forEach((formId) => {
        const form = document.getElementById(formId);
        if (!form) return;
        form.classList.toggle(
          'booking-form-selection-gate',
          Boolean(this.selectionGate) && formId === openId,
        );
      });
    },
    async markListingCoverage(rateSetData, unitId) {
      const results = await Promise.all(
        rateSetData.map(async (rateSet) => [
          String(rateSet.id),
          await this.checkDateAvailability(rateSet.id, unitId),
        ]),
      );
      this.listingCoverage = Object.fromEntries(results);
      this.paintListingCoverage();
    },
    paintListingCoverage() {
      const coverage = this.listingCoverage;
      if (!coverage) return;
      const formId =
        this.product === 'Tour' ? 'addTourForm' : 'addAccommodationForm';
      const dropdown = document.querySelector(`#${formId} [name="rate_set"]`);
      if (!dropdown) return;
      Array.from(dropdown.options).forEach((option) => {
        if (!option.value) return;
        const unavailable = coverage[String(option.value)] === false;
        option.classList.toggle('listing-option-muted', unavailable);
        option.style.color = unavailable ? '#b0b0b0' : '';
      });
    },
    clearListingMute() {
      this.listingCoverage = null;
      ['addTourForm', 'addAccommodationForm'].forEach((formId) => {
        const dropdown = document.querySelector(`#${formId} [name="rate_set"]`);
        if (!dropdown) return;
        Array.from(dropdown.options).forEach((option) => {
          option.classList.remove('listing-option-muted');
          option.style.color = '';
        });
      });
    },
    async reconcileSelectionAfterDateChange() {
      if (
        !this.prefilledStart ||
        !this.soldAs ||
        !this.startDate ||
        !this.endDate
      )
        return;
      const datesAvailable = await this.checkDateAvailability(
        this.soldAs,
        this.unitId,
      );
      if (datesAvailable) {
        this.selectionGate = false;
        this.clearListingMute();
        this.syncSelectionGateClass();
        this.updatePrice();
        return;
      }
      this.selectionGate = true;
      this.syncSelectionGateClass();
      if (this.soldAsData?.length) {
        await this.markListingCoverage(this.soldAsData, this.unitId);
      }
    },
    removeListingPrompt() {
      document
        .querySelectorAll(
          '#addTourForm [name="rate_set"] option[value=""], #addAccommodationForm [name="rate_set"] option[value=""]',
        )
        .forEach((option) => option.remove());
    },
    applyPrefilledPickerDates() {
      const instance = this.flatpickrInstances[this.product];
      if (!instance || !this.startDate || !this.endDate) return;
      const start = parseDate(this.startDate);
      const end = parseDate(this.endDate);
      if (!start || !end) return;
      try {
        instance.setDate(
          this.startDate === this.endDate ? [start] : [start, end],
          false,
        );
      } catch (error) {
        console.error('Could not prefill the date picker', error);
      }
    },
    finishDateSelectionListing(datesAvailable, keptStart, keptEnd) {
      if (!this.prefilledStart || !this.prefilledEnd) return;
      if (datesAvailable) {
        this.selectionGate = false;
        this.clearListingMute();
        this.removeListingPrompt();
      } else {
        this.startDate = keptStart || this.prefilledStart;
        this.endDate = keptEnd || this.prefilledEnd;
        if (this.pinnedUnitCount) this.units = this.pinnedUnitCount;
        this.selectionGate = true;
      }
      this.applyPrefilledPickerDates();
      this.syncSelectionGateClass();
      this.paintListingCoverage();
    },
    async openFromDateSelection(selection) {
      const dates = [...(selection.dates || [])].filter(Boolean).sort();
      if (!dates.length) return;

      closeAllForms();
      this.resetStore();
      this.price = 0;

      const calendarStore = Alpine.store('calendarStore');
      const selectedCalendar = calendarStore.calendar_selected;
      const selectedCalendarId =
        typeof selectedCalendar === 'string'
          ? selectedCalendar
          : selectedCalendar?.id;
      this.calendarId =
        selectedCalendarId ||
        document
          .querySelector('.calendar-container[data-calendar-id]')
          ?.getAttribute('data-calendar-id') ||
        document
          .querySelector('.calendar-grid[data-calendar-id]')
          ?.getAttribute('data-calendar-id') ||
        null;
      this.product = selection.productType;
      this.productId = selection.productId;
      if (!this.calendarId || !this.product) {
        this.setSubmitMessage(
          'Unable to load booking form. Please refresh the page and try again.',
          'error',
        );
        return;
      }

      const startDate = dates[0];
      const lastDate = dates[dates.length - 1];
      const endDate =
        this.product === 'Accommodation'
          ? formatDateISO(addDays(lastDate, 1))
          : lastDate;
      const unitIds = (selection.unitIds || []).map(String);
      const unitId = selection.unitId || null;
      const rateUnitId = selection.rateUnitId || unitId;
      if (!rateUnitId) {
        this.setSubmitMessage(
          'Unable to load booking form. Please refresh the page and try again.',
          'error',
        );
        return;
      }

      this.dateClicked = startDate;
      this.unitId = unitId;
      this.tourDepartureId = selection.departureId || null;
      this.tourDepartureTime = selection.departureTime || '';
      this.units = Math.max(unitIds.length, 1);

      const formId =
        this.product === 'Accommodation'
          ? 'addAccommodationForm'
          : 'addTourForm';
      const fieldData = {
        start_date: startDate,
        end_date: endDate,
        accommodation_unit_id: unitId || '',
        tour_unit_id: unitId || '',
        tour_departure_id: selection.departureId || '',
      };

      Alpine.store('cartStore').checkEditMode();
      await this.renderForm();
      openForm(formId, 'open', fieldData, null, null);
      document.body.classList.add('slideform-open');

      if (this.flatpickrInstances[this.product]) {
        this.flatpickrInstances[this.product].clear();
        this.flatpickrInstances[this.product].set('disable', [() => true]);
      }

      this.startInitializing();
      try {
        const rateSetData = await this.getRateSetDataOnDemand(
          rateUnitId,
          startDate,
        );
        this.soldAsData = rateSetData;
        if (!rateSetData || rateSetData.length === 0) {
          this.setSubmitMessage(
            'No listings available for this date. Please select a different date or check your rate set configuration.',
            'error',
          );
          return;
        }

        this.prefilledStart = startDate;
        this.prefilledEnd = endDate;
        this.pinnedUnitIds = unitIds.length > 1 ? unitIds : [];
        this.pinnedUnitCount = unitIds.length > 1 ? unitIds.length : null;
        this.startDate = startDate;
        this.endDate = endDate;
        this.units = this.pinnedUnitCount || 1;

        const first = rateSetData[0];
        this.soldAs = first.id;
        this.soldAsType = first.type;
        this.isPackage = first.is_package || false;
        this.totalUnits = first.units_count;
        this.peopleAsOption = first.people_as_option;
        if (this.peopleAsOption) {
          this.selectedPeople = 1;
          this.peoplePerUnit = first.people_per_unit;
          this.generateAmountPeopleOptions();
        }
        this.usePersonTypes = first.use_person_types;

        populateRateSetDropdown(rateSetData);
        this.initializeAllFlatpickrs(first.date_select);
        await this.handleRateChange(first, unitId);
        populateAmountUnitsDropdown(this.totalUnits, this.product);
        if (this.pinnedUnitCount) {
          this.units = this.pinnedUnitCount;
          const amount = document.querySelector(
            `#${formId} [name="amount_units"]`,
          );
          if (amount) amount.value = String(this.pinnedUnitCount);
          this.fillPinnedPersonAdults();
        }
        updateReferrerFields();
      } finally {
        this.stopInitializing();
      }
    },
    async handleCalendarDateClick(e) {
      if (e && Array.isArray(e.dates)) {
        return this.openFromDateSelection(e);
      }
      console.log('FUNCTION CALLED: handleCalendarDateClick');

      closeAllForms();
      this.resetStore();
      this.units = 1;
      this.price = 0;

      const clickedDateElement =
        e.currentTarget?.closest('.calendar-date') ||
        e.target?.closest('.calendar-date');
      if (!clickedDateElement) {
        console.error('Calendar date element not found for click event');
        return;
      }

      // Get calendar ID from the calendar store instead of relying on data attribute
      // This ensures it's always available even for unavailable dates
      const calendarStore = Alpine.store('calendarStore');
      this.calendarId =
        calendarStore.calendar_selected?.id ||
        clickedDateElement.getAttribute('data-calendar-id') ||
        document
          .querySelector('.calendar-grid[data-calendar-id]')
          ?.getAttribute('data-calendar-id') ||
        document
          .querySelector('.calendar-container[data-calendar-id]')
          ?.getAttribute('data-calendar-id') ||
        document
          .querySelector('.calendar-container')
          ?.getAttribute('data-calendar-id');
      this.productId = clickedDateElement.getAttribute('data-product-id');

      this.product = clickedDateElement.getAttribute('data-product');
      if (!this.product) {
        console.error('No product found on clicked calendar date element');
        return;
      }

      // Ensure we have a valid calendar ID before proceeding
      if (!this.calendarId) {
        console.error('No calendar ID available for form rendering');
        return;
      }

      const date = clickedDateElement.getAttribute('data-date');
      if (!date) {
        console.error('No date found on clicked calendar date element');
        return;
      }
      this.dateClicked = date;
      console.log('dateClicked:', date);

      const unitId = clickedDateElement.getAttribute('data-unit-id') || null;
      const rateUnitId =
        unitId || clickedDateElement.getAttribute('data-rate-unit-id') || null;
      const tourDepartureId =
        clickedDateElement.getAttribute('data-tour-departure-id') || null;
      this.tourDepartureId = tourDepartureId;
      this.tourDepartureTime =
        clickedDateElement.getAttribute('data-tour-departure-time') || '';
      console.log('unitId:', unitId);

      // A scheduled row has a time slot, not a unit. rateUnitId only resolves listings.
      if (!rateUnitId) {
        console.error(
          'No unit ID found on clicked element. Check data-unit-id attribute.',
        );
        this.setSubmitMessage(
          'Unable to load booking form. Please refresh the page and try again.',
          'error',
        );
        return;
      }

      let formIdToOpen =
        this.product === 'Accommodation'
          ? 'addAccommodationForm'
          : 'addTourForm';
      const fieldData = {
        start_date: date,
        accommodation_unit_id: unitId || '',
        tour_unit_id: unitId || '',
        tour_departure_id: tourDepartureId || '',
      };

      const cartStore = Alpine.store('cartStore');
      cartStore.checkEditMode();

      await this.renderForm();
      openForm(formIdToOpen, 'open', fieldData, null, null);
      document.body.classList.add('slideform-open'); // This is to prevent the background from scrolling when the form is open

      if (this.flatpickrInstances[this.product]) {
        this.flatpickrInstances[this.product].clear();
        this.flatpickrInstances[this.product].set('disable', [() => true]);
      }

      this.startInitializing();
      try {
        // OPTIMIZED: Fetch rate data on-demand from new lightweight endpoint
        // This reduces initial page load by only fetching rate data when calendar-date is clicked
        const rateSetData = await this.getRateSetDataOnDemand(rateUnitId, date);
        console.log('rateSetData:', rateSetData);
        this.soldAsData = rateSetData;

        // Guard clause: if no rate sets are available, show message and return
        if (!rateSetData || rateSetData.length === 0) {
          console.warn(
            'No rate sets available for this date/unit. unitId:',
            unitId,
            'date:',
            date,
          );
          this.setSubmitMessage(
            'No listings available for this date. Please select a different date or check your rate set configuration.',
            'error',
          );
          return;
        }

        this.soldAs = rateSetData.length > 0 ? rateSetData[0].id : null;
        this.soldAsType = rateSetData.length > 0 ? rateSetData[0].type : null;
        this.isPackage =
          rateSetData.length > 0 ? rateSetData[0].is_package || false : false;
        const totalUnits =
          rateSetData.length > 0 ? rateSetData[0].units_count : null;
        console.log('totalUnits:', totalUnits);
        this.totalUnits = totalUnits;
        this.peopleAsOption =
          rateSetData.length > 0 ? rateSetData[0].people_as_option : null;

        if (this.peopleAsOption) {
          console.log('peopleAsOption is true, setting selectedPeople to 1');
          this.selectedPeople = 1;
          this.peoplePerUnit =
            rateSetData.length > 0 ? rateSetData[0].people_per_unit : null;
          console.log('peoplePerUnit:', this.peoplePerUnit);
          this.generateAmountPeopleOptions();
        }
        this.usePersonTypes =
          rateSetData.length > 0 ? rateSetData[0].use_person_types : null;
        console.log('usePersonTypes:', this.usePersonTypes);

        populateRateSetDropdown(rateSetData);
        this.initializeAllFlatpickrs(rateSetData[0].date_select);
        this.handleRateChange(rateSetData[0], unitId);
        populateAmountUnitsDropdown(totalUnits, this.product);
        updateReferrerFields();
      } finally {
        this.stopInitializing();
      }
    },
    async renderForm() {
      // Render the form on the DOM
      let formContainer;
      let addBookingForm;
      let url;

      if (this.product === 'Accommodation') {
        formContainer = document.getElementById(
          'add-accommodation-form-container',
        );
        addBookingForm = document.getElementById('addAccommodationForm');
        url = `/calendar/add-accommodation-form/${this.calendarId}/`;
      } else if (this.product === 'Tour') {
        formContainer = document.getElementById('add-tour-form-container');
        addBookingForm = document.getElementById('addTourForm');
        url = `/calendar/add-tour-form/${this.calendarId}/`;
      }

      if (addBookingForm) {
        const hasLoadingShell =
          addBookingForm.querySelector('[data-loading-overlay="true"]') &&
          addBookingForm.querySelector('[data-loading-content="true"]');
        if (hasLoadingShell) {
          // Form is already rendered, just open it
          return; // Exit the function early
        }
        // Backward compatibility: replace stale in-DOM form markup rendered before loading-shell fixes.
        addBookingForm.remove();
      }

      // Fetch the product form from the server
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      });

      // Use safe JSON parsing to handle auth errors gracefully
      const data = await safeJsonParse(response);
      // Update the form container with the fetched HTML
      formContainer.innerHTML = data.add_booking_form_html;
    },
    async preloadFormShells(calendarId) {
      const resolvedCalendarId =
        calendarId || Alpine.store('calendarStore')?.calendar_selected?.id;
      if (!resolvedCalendarId) {
        return;
      }

      const preloadTargets = [
        {
          formId: 'addAccommodationForm',
          containerId: 'add-accommodation-form-container',
          url: `/calendar/add-accommodation-form/${resolvedCalendarId}/`,
        },
        {
          formId: 'addTourForm',
          containerId: 'add-tour-form-container',
          url: `/calendar/add-tour-form/${resolvedCalendarId}/`,
        },
      ];

      await Promise.all(
        preloadTargets.map(async ({ formId, containerId, url }) => {
          const existingForm = document.getElementById(formId);
          if (
            existingForm &&
            existingForm.querySelector('[data-loading-overlay="true"]') &&
            existingForm.querySelector('[data-loading-content="true"]')
          ) {
            return;
          }

          const container = document.getElementById(containerId);
          if (!container) {
            return;
          }

          try {
            const response = await fetch(url, {
              method: 'GET',
              headers: {
                'X-CSRFToken': getCSRFToken(),
              },
            });
            const data = await safeJsonParse(response);
            container.innerHTML = data.add_booking_form_html;
          } catch (error) {
            console.warn(`Failed preloading ${formId} shell`, error);
          }
        }),
      );
    },
    syncSelectedDeparture(departureId) {
      const nextId = departureId ? String(departureId) : '';
      const match = (this.departureOptions || []).find(
        (departure) => String(departure.id) === nextId,
      );
      this.tourDepartureId = nextId || null;
      this.tourDepartureTime = match?.time || '';
      const hidden = document.querySelector(
        '#addTourForm input[name="tour_departure_id"]',
      );
      if (hidden) hidden.value = nextId;
    },
    renderDepartureOptions() {
      const paint = () => {
        const select = document.getElementById('id_add_tour_departure');
        if (!select) return;
        const selected = String(this.tourDepartureId || '');
        select.replaceChildren();
        (this.departureOptions || []).forEach((departure) => {
          const option = document.createElement('option');
          option.value = String(departure.id);
          option.textContent = departure.display_name || departure.time || '';
          select.appendChild(option);
        });
        const stillValid = (this.departureOptions || []).some(
          (departure) => String(departure.id) === selected,
        );
        const nextId = stillValid
          ? selected
          : this.departureOptions[0]
          ? String(this.departureOptions[0].id)
          : '';
        select.value = nextId;
        this.syncSelectedDeparture(nextId);
      };
      paint();
      requestAnimationFrame(paint);
    },
    componentDepartureLabel(group) {
      const tourName = (group?.tour_name || group?.listing_name || '').trim();
      return tourName ? `Time for ${tourName}` : 'Time';
    },
    paintPackageDepartureOptions() {
      const paint = () => {
        (this.componentDepartureGroups || []).forEach((group) => {
          const select = document.getElementById(
            `package-departure-${group.id}`,
          );
          if (!select) return;
          const selected = String(
            this.selectedComponentDepartures[group.id] || '',
          );
          select.replaceChildren();
          const prompt = document.createElement('option');
          prompt.value = '';
          prompt.textContent = 'Choose a time';
          select.appendChild(prompt);
          (group.departures || []).forEach((departure) => {
            const option = document.createElement('option');
            option.value = String(departure.id);
            option.textContent = departure.display_name || departure.time || '';
            select.appendChild(option);
          });
          select.value = selected;
        });
      };
      const paintLater = () => {
        paint();
        requestAnimationFrame(paint);
      };
      if (window.Alpine && Alpine.nextTick) Alpine.nextTick(paintLater);
      else paintLater();
    },
    selectComponentDeparture(componentId, departureId) {
      this.selectedComponentDepartures = {
        ...this.selectedComponentDepartures,
        [componentId]: departureId ? String(departureId) : '',
      };
    },
    clearPackageComponentDepartures() {
      this.componentDepartureRequestId += 1;
      this.componentDepartureGroups = [];
      this.selectedComponentDepartures = {};
    },
    async loadPackageComponentDepartures() {
      const requestId = ++this.componentDepartureRequestId;
      if (!this.isPackage || !this.soldAs || !this.startDate || !this.endDate) {
        this.componentDepartureGroups = [];
        this.selectedComponentDepartures = {};
        return;
      }
      const previous = { ...this.selectedComponentDepartures };
      try {
        const response = await postJSONWithCSRFRetry(
          '/bookings_app/get-available-departures/',
          {
            soldAs: this.soldAs,
            startDate: this.startDate,
            endDate: this.endDate,
            amountUnits: this.units,
            amountPeople: this.selectedPeople || this.units,
            context: 'admin',
          },
        );
        const data = await response.json();
        if (requestId !== this.componentDepartureRequestId) return;
        const groups = data.components || [];
        const next = {};
        groups.forEach((group) => {
          const departures = group.departures || [];
          const previousId = previous[group.id];
          const stillAvailable = departures.some(
            (departure) => String(departure.id) === String(previousId),
          );
          next[group.id] = stillAvailable
            ? String(previousId)
            : departures.length === 1
            ? String(departures[0].id)
            : '';
        });
        this.componentDepartureGroups = groups;
        this.selectedComponentDepartures = next;
        this.paintPackageDepartureOptions();
      } catch (error) {
        if (requestId !== this.componentDepartureRequestId) return;
        console.error('Error loading package time slots:', error);
        this.componentDepartureGroups = [];
        this.selectedComponentDepartures = {};
      }
    },
    applyListingDepartures(rateSet) {
      const departures = Array.isArray(rateSet?.departures)
        ? rateSet.departures
        : [];
      this.departureOptions = rateSet?.is_scheduled ? departures : [];
      if (!this.departureOptions.length) {
        this.syncSelectedDeparture('');
        return;
      }
      this.renderDepartureOptions();
    },
    loadListingDepartures(rateSetId) {
      const requestId = ++this.departureRequestId;
      if (this.product !== 'Tour' || !rateSetId) {
        this.applyListingDepartures({ is_scheduled: false, departures: [] });
        return Promise.resolve();
      }
      return fetch(`/rates/rate-set/${rateSetId}/`)
        .then(async (response) => safeJsonParse(response))
        .then((data) => {
          if (requestId !== this.departureRequestId) return;
          if (String(this.soldAs) !== String(rateSetId)) return;
          this.applyListingDepartures(data);
        })
        .catch((error) => console.error('Error loading time slots:', error));
    },
    async handleDepartureChange(event) {
      this.syncSelectedDeparture(event.target.value);
      if (!this.startDate || !this.endDate) return;
      const datesAvailable = await this.checkDateAvailability(
        this.soldAs,
        this.unitId,
      );
      if (!datesAvailable) {
        this.resetDatesAndPrice(true);
        this.resetFlatpickrDates();
        this.setValidationError(
          'startEndDate',
          'The selected dates are not available for this time slot.',
        );
        return;
      }
      const instance = this.flatpickrInstances[this.product];
      if (instance) {
        this.fetchUnavailableDates(
          instance.currentYear.toString(),
          (instance.currentMonth + 1).toString(),
          instance,
        );
      }
    },
    updateSelectedRate(flatpickrElement) {
      console.log('FUNCTION CALLED: updateSelectedRate');

      // Get currently selected RateSet
      const soldAsId = this.soldAs;
      const unitId = this.unitId;
      console.log('soldAs ID:', soldAsId);

      // Find the corresponding RateSet object from this.soldAsData
      const selectedRateSet = this.soldAsData.find(
        (soldAs) => soldAs.id === soldAsId,
      );

      // Store the full rate set object (includes active_custom_fields from RateSetSerializer)
      this.selectedRateSet = selectedRateSet;

      this.peopleAsOption = selectedRateSet.people_as_option;
      this.usePersonTypes = selectedRateSet.use_person_types;

      // Update the soldAsType when a different rate is selected
      this.soldAsType = selectedRateSet.type;

      // Track if selected rate set is a package
      this.isPackage = selectedRateSet.is_package || false;

      // Reset custom fields when rate set changes
      // Using object approach - getPersonFieldsObj() creates entries on demand
      this.customFields = {};
      this.customFieldsPerPerson = {};

      if (selectedRateSet) {
        console.log('Selected RateSet:', selectedRateSet);
        console.log('Custom Fields:', selectedRateSet.active_custom_fields);
        // Pass the entire RateSet object to handleRateChange
        this.handleRateChange(selectedRateSet, unitId, flatpickrElement);
      } else {
        console.error('Selected RateSet not found in soldAsData.');
      }
    },
    async handleRateChange(selectedRateSet, unitId, flatpickrElement) {
      console.log('FUNCTION CALLED: handleRateChange');
      console.log('selectedRateSet:', selectedRateSet);

      // Store the full rate set object (includes active_custom_fields)
      this.selectedRateSet = selectedRateSet;
      console.log(
        'Updated selectedRateSet in handleRateChange:',
        this.selectedRateSet,
      );

      // Store current dates before any changes
      const currentStartDate = this.startDate;
      const currentEndDate = this.endDate;

      this.peoplePerUnit = selectedRateSet.people_per_unit;
      console.log('peoplePerUnit set to:', this.peoplePerUnit);
      console.log('usePersonTypes:', this.usePersonTypes);
      console.log('soldAsType:', this.soldAsType);

      // Show tour duration toggle only for tours with range mode and not fixed
      this.showTourDurationToggle =
        this.product === 'Tour' &&
        selectedRateSet.date_select === 'range' &&
        !selectedRateSet.is_fixed;

      if (this.showTourDurationToggle) {
        const start = this.prefilledStart || currentStartDate;
        const end = this.prefilledEnd || currentEndDate;
        this.tourDuration = start && end && start !== end ? 'multi' : 'single';
      }

      // Helper to check if unit type is a person type (Person, Adult, or Child)
      const isPersonUnitType =
        this.soldAsType === 'Person' ||
        this.soldAsType === 'Adult' ||
        this.soldAsType === 'Child';

      // Generate person type options if usePersonTypes is true (for any unit type)
      if (this.usePersonTypes) {
        console.log('Generating person type options');
        if (isPersonUnitType) {
          const totalUnits = parseInt(selectedRateSet.units_count);
          this.generatePersonTypeOptions(totalUnits);
          // Initialize with 1 adult by default for Person/Adult/Child unit types
          this.selectedAdults = 1;
          this.selectedChildren = 0;
          this.units = 1;
        } else {
          // Reset units to 1 when switching to a rate with usePersonTypes
          this.units = 1;
          // Initialize with 1 adult by default
          this.selectedAdults = 1;
          this.selectedChildren = 0;
          // For non-Person types, call without totalUnits
          this.generatePersonTypeOptions();
        }
      } else if (this.peoplePerUnit != null) {
        console.log('Generating amount people options');
        // Reset units to 1 when switching to a rate with peoplePerUnit
        this.units = 1;
        this.generateAmountPeopleOptions();
      } else {
        // For Person/Adult/Child types without usePersonTypes or peoplePerUnit,
        // default to 1 unit (1 person)
        console.log(
          'Setting default units to 1 for unit type:',
          this.soldAsType,
        );
        this.units = 1;
      }

      // customFieldsPerPerson is now an object - getPersonFieldsObj() creates entries on demand
      // No need to pre-initialize based on people count

      const totalUnits = selectedRateSet.units_count;
      populateAmountUnitsDropdown(totalUnits, this.product);

      if (this.product === 'Tour') {
        await this.loadListingDepartures(selectedRateSet.id);
      }

      if (this.pinnedUnitCount) {
        this.units = this.pinnedUnitCount;
        this.fillPinnedPersonAdults();
      }

      // Check if current dates are available before clearing
      const datesAvailable = await this.checkDateAvailability(
        selectedRateSet.id,
        unitId,
      );
      this.resetDatesAndPrice(!datesAvailable);

      if (datesAvailable) {
        // Keep the current dates
        console.log('Selected dates are available, preserving them');
        // Restore the dates that were stored
        this.startDate = currentStartDate;
        this.endDate = currentEndDate;
        // Update price with preserved dates
        this.updatePrice();
      } else {
        console.log(
          'Selected dates NOT available or no dates selected, clearing dates',
        );
      }

      if (selectedRateSet.is_fixed) {
        // Logic to display a dropdown with available fixed date ranges
        this.showFixedDateRange = true;
        // Populate dropdown with options
        this.populateFixedDateRangeDropdown(this.product, selectedRateSet.id);
      } else {
        // Show Flatpickr for non-fixed soldAs
        this.showFixedDateRange = false;

        if (!datesAvailable) {
          // Reset Flatpickr dates here if needed
          this.resetFlatpickrDates(selectedRateSet.id);
          this.updateInitialDateForIdentifier(this.product, this.dateClicked);
        }

        // Determine the actual mode to use for flatpickr initialization
        let flatpickrMode = selectedRateSet.date_select || 'range';
        if (this.showTourDurationToggle) {
          // For tours with range capability, use the user's selection
          flatpickrMode = this.tourDuration === 'single' ? 'single' : 'range';
        }

        if (flatpickrElement) {
          this.initializeFlatpickr(
            this.product,
            flatpickrElement,
            flatpickrMode,
          );
        } else {
          // Initialize all Flatpickr elements if the specific one is not found
          this.initializeAllFlatpickrs(flatpickrMode);
        }
      }
      this.finishDateSelectionListing(
        datesAvailable,
        currentStartDate,
        currentEndDate,
      );
      if (
        this.prefilledStart &&
        this.selectionGate &&
        this.soldAsData?.length
      ) {
        await this.markListingCoverage(this.soldAsData, unitId);
      }
      await this.loadPackageComponentDepartures();
    },
    initializeAllFlatpickrs(dateSelectMode) {
      console.log('FUNCTION CALLED: initializeAllFlatpickrs');
      Object.keys(this.flatpickrInstances).forEach((product) => {
        const instance = this.flatpickrInstances[product].element;
        if (instance) {
          this.initializeFlatpickr(product, instance, dateSelectMode);
        }
      });
    },
    async handleUnitChange() {
      console.log('FUNCTION CALLED: handleUnitChange');
      if (this.isInitializing) return;
      if (
        this.pinnedUnitIds.length &&
        Number(this.units) !== this.pinnedUnitIds.length
      ) {
        this.pinnedUnitIds = [];
        this.pinnedUnitCount = null;
      }

      const { start: currentStartDate, end: currentEndDate } =
        this.selectedDatesSnapshot();

      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.soldAsType === 'Person' ||
        this.soldAsType === 'Adult' ||
        this.soldAsType === 'Child';

      // Rebuilding these selects can emit change events. Ignore them here so
      // they don't wipe the dates before the availability check runs.
      this.suppressUnitDateCheck = true;
      try {
        if (this.usePersonTypes) {
          console.log('Generating person type options after unit change');
          if (isPersonUnitType) {
            // For Person/Adult/Child type, pass the new units value
            this.generatePersonTypeOptions(this.units);
          } else {
            // For other types, recalculate based on new units * peoplePerUnit
            this.generatePersonTypeOptions();
          }
        } else if (this.peoplePerUnit != null) {
          console.log('Generating amount people options');
          this.generateAmountPeopleOptions();
        }
      } finally {
        this.suppressUnitDateCheck = false;
      }

      // Validate people vs units after unit change
      this.validatePeopleUnits();
      await this.settleDatesForUnitCount(currentStartDate, currentEndDate);
    },
    selectedDatesSnapshot() {
      let start = this.startDate || '';
      let end = this.endDate || '';
      const selected =
        this.flatpickrInstances[this.product]?.selectedDates || [];
      if (selected.length) {
        const pickerStart = formatDateISO(selected[0]);
        const pickerEnd = formatDateISO(selected[selected.length - 1]);
        if (!start) start = pickerStart;
        if (!end) end = pickerEnd || pickerStart;
      }
      return { start, end };
    },
    async settleDatesForUnitCount(currentStartDate, currentEndDate) {
      if (!currentStartDate || !currentEndDate) {
        this.updatePrice();
        this.loadPackageComponentDepartures();
        return;
      }

      this.startDate = currentStartDate;
      this.endDate = currentEndDate;
      const datesAvailable = await this.checkDateAvailability(
        this.soldAs,
        this.unitId,
      );
      this.resetDatesAndPrice(!datesAvailable);

      if (datesAvailable) {
        console.log('Selected dates are available, preserving them');
        this.startDate = currentStartDate;
        this.endDate = currentEndDate;
        this.clearValidationError('startEndDate');

        if (this.showFixedDateRange === true) {
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        } else {
          const instance = this.flatpickrInstances[this.product];
          if (instance) {
            // Ask with the arrival already chosen so a valid checkout stays
            // enabled, then put the same range back on the picker.
            await this.fetchUnavailableDates(
              instance.currentYear.toString(),
              (instance.currentMonth + 1).toString(),
              instance,
              parseDate(currentStartDate),
            );
            this.startDate = currentStartDate;
            this.endDate = currentEndDate;
            instance.set('allowInvalidPreload', true);
            this.applyPrefilledPickerDates();
            instance.set('allowInvalidPreload', false);
          }
          this.updatePrice();
        }
      } else {
        console.log(
          'Selected dates NOT available for the new unit count, clearing dates',
        );
        const unitWord = Number(this.units) === 1 ? 'unit' : 'units';
        const dateInterval =
          currentStartDate === currentEndDate
            ? currentStartDate
            : `${currentStartDate} to ${currentEndDate}`;
        this.setValidationError(
          'startEndDate',
          `Selected dates ${dateInterval} not available for ${this.units} ${unitWord}`,
        );

        if (this.showFixedDateRange === true) {
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        } else {
          this.resetFlatpickrDates();
          this.updateInitialDateForIdentifier(this.product, this.dateClicked);
        }
      }
      this.loadPackageComponentDepartures();
    },
    generateAmountPeopleOptions() {
      console.log('FUNCTION CALLED: generateAmountPeopleOptions');

      if (this.peoplePerUnit == null || this.units == null) {
        console.warn('peoplePerUnit or units is null, cannot generate options');
        this.amountPeopleOptions = [];
        return;
      }

      const maxPeople = this.peoplePerUnit * this.units;
      console.log('this.peoplePerUnit:', this.peoplePerUnit);
      console.log('this.units:', this.units);
      console.log('maxPeople:', maxPeople);

      this.amountPeopleOptions = Array.from(
        { length: maxPeople },
        (_, i) => i + 1,
      );

      if (this.peopleAsOption == true) {
        // Keep current selection if valid, otherwise reset to minimum
        if (!this.selectedPeople || this.selectedPeople > maxPeople) {
          this.selectedPeople = 1;
        }
      } else {
        this.selectedPeople = null;
      }
    },
    handleAmountPeopleChange() {
      console.log('FUNCTION CALLED: handleAmountPeopleChange');
      // customFieldsPerPerson is an object - getPersonFieldsObj() creates entries on demand
      // No need to pre-initialize

      // Validate people vs units after people change
      this.validatePeopleUnits();

      if (this.showFixedDateRange === true) {
        // Populate dropdown with options
        this.populateFixedDateRangeDropdown(this.product, this.soldAs);
      }
      // Don't reset dates for people changes - only update price
      this.updatePrice();
      this.loadPackageComponentDepartures();
    },
    populateFixedDateRangeDropdown(product, soldAsId) {
      console.log('FUNCTION CALLED: populateFixedDateRangeDropdown');
      const csrfToken = getCSRFToken();
      fetch('/bookings_app/fixed-date-ranges/', {
        method: 'POST',
        body: JSON.stringify({
          soldAs: soldAsId,
          amountUnits: this.units,
          selectedPeople: this.selectedPeople,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          let dropdown = null;
          if (product === 'Accommodation') {
            dropdown = document.querySelector(
              ' #addAccommodationForm .fixedDateRangeDropdown',
            );
          } else {
            dropdown = document.querySelector(
              '#addTourForm .fixedDateRangeDropdown',
            );
          }

          // Check if dropdown is found
          if (!dropdown) {
            console.error('Dropdown not found for product:', product);
            return; // Exit if dropdown is not found
          }

          // Clear existing options
          dropdown.innerHTML = '';

          // Combine available and unavailable date ranges
          const allRanges = [
            ...data.available_fixed_date_ranges.map((range) => ({
              ...range,
              available: true,
            })),
            ...data.unavailable_fixed_date_ranges.map((range) => ({
              ...range,
              available: false,
            })),
          ];

          // Sort the combined array by start date and then by end date
          allRanges.sort((a, b) => {
            const startDateComparison =
              new Date(a.date.split('/').reverse().join('-')) -
              new Date(b.date.split('/').reverse().join('-'));
            if (startDateComparison !== 0) return startDateComparison;
            return (
              new Date(a.end_date.split('/').reverse().join('-')) -
              new Date(b.end_date.split('/').reverse().join('-'))
            );
          });

          // Create and append the first disabled option
          const firstOption = document.createElement('option');
          if (allRanges.length === 0) {
            firstOption.textContent = 'No dates available at this time';
          } else {
            firstOption.textContent = 'Select date';
          }
          firstOption.style.color = 'grey';
          firstOption.disabled = true;
          firstOption.selected = true; // Make this option selected by default
          dropdown.appendChild(firstOption);

          // Append sorted date ranges to the dropdown
          allRanges.forEach((range) => {
            const option = document.createElement('option');
            option.value = `${range.date}-${range.end_date}`;

            // Check if start and end dates are the same (single day)
            const isSingleDay = range.date === range.end_date;

            if (isSingleDay) {
              // For single day tours, show only one date
              option.textContent = `${range.date}${
                range.available ? '' : ' (unavailable)'
              }`;
            } else {
              // For multi-day tours, show date range
              option.textContent = `${range.date} to ${range.end_date} ${
                range.available ? '' : ' (unavailable)'
              }`;
            }

            option.setAttribute('data-start-date', range.date);
            option.setAttribute('data-end-date', range.end_date);
            if (!range.available) {
              option.disabled = true; // Disable the option if unavailable
              option.style.color = 'grey'; // Optionally style the unavailable option
            }
            dropdown.appendChild(option);
          });

          // Ensure "Select date" remains selected after populating options
          firstOption.selected = true; // Explicitly set the first option to selected again

          console.log('Fixed Rates:', data);
          // Optionally, reset the fixedDateRange if you want to force user selection after adding the first option
          this.fixedDateRange = '';
          this.updatePrice();
        })
        .catch((error) =>
          console.error('Error fetching fixed date ranges:', error),
        );
    },
    fixedDateSelection() {
      console.log('FUNCTION CALLED: fixedDateSelection');

      let dropdown = null;
      if (this.product === 'Accommodation') {
        dropdown = document.querySelector(
          ' #addAccommodationForm .fixedDateRangeDropdown',
        );
      } else {
        dropdown = document.querySelector(
          '#addTourForm .fixedDateRangeDropdown',
        );
      }
      const selectedOption = dropdown.options[dropdown.selectedIndex];
      if (selectedOption) {
        // Extract the dates from the option's attributes
        const startDateStr = selectedOption.getAttribute('data-start-date');
        const endDateStr = selectedOption.getAttribute('data-end-date');

        // Convert dates from DD/MM/YYYY to YYYY-MM-DD format
        const startDate = this.convertDateToISO(startDateStr);
        const endDate = this.convertDateToISO(endDateStr);

        // Update the store's date properties
        this.startDate = startDate;
        this.endDate = endDate;
        console.log(
          'Updated startDate and endDate:',
          this.startDate,
          this.endDate,
        );
        this.updatePrice();
        // Clear date warning if both dates are set
        if (this.startDate && this.endDate) {
          this.clearValidationError('startEndDate');
          if (this.prefilledStart) this.reconcileSelectionAfterDateChange();
          if (this.isPackage) this.loadPackageComponentDepartures();
        }
      }
    },
    initializeFlatpickr(product, flatpickrElement, mode = 'range') {
      console.log('FUNCTION CALLED: initializeFlatpickr');
      //console.log('Initializing Flatpickr:', product, flatpickrElement, mode);
      this.flatpickrInstances[product] = flatpickr(flatpickrElement, {
        disableMobile: 'true',
        mode: mode,
        dateFormat: 'd/m/Y',
        showMonths:
          mode === 'range'
            ? window.matchMedia('(min-width: 769px)').matches
              ? 2
              : 1
            : 1, // Show two months on desktop, one on mobile for range selection

        onOpen: (selectedDates, dateStr, instance) => {
          // Add loading class immediately when opening
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          instance.clear();
          // Reset all date restrictions
          instance.set('disable', []); // Clear all existing restrictions
          instance.set('minDate', null); // Clear any minimum date restriction
          instance.set('maxDate', null); // Clear any maximum date restriction
          instance.set('disable', [
            function () {
              return true;
            },
          ]);
          this.updateInitialDateForIdentifier(this.product, this.dateClicked);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1;
          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            this.startDate,
          );

          this.updateFlatpickrDates(product, this.startDate);
        },
        onChange: (selectedDates, dateStr, instance) => {
          this.startDate = selectedDates[0]
            ? formatDateISO(selectedDates[0]) // Timezone-safe YYYY-MM-DD format
            : '';
          this.endDate =
            mode === 'single'
              ? this.startDate
              : selectedDates[1]
              ? formatDateISO(selectedDates[1])
              : '';

          const startDate = selectedDates[0];

          // Enable nextMonth arrow
          if (instance && instance.nextMonthNav) {
            instance.nextMonthNav.classList.remove('flatpickr-disabled'); // Remove disabled class
            instance.nextMonthNav.setAttribute('aria-disabled', 'false'); // Reset accessibility attribute
            instance.nextMonthNav.onclick = null; // Re-enable navigation
          }

          // Re-enable the year selector
          if (instance && instance.currentYearElement) {
            instance.currentYearElement.removeAttribute('disabled'); // Re-enable the year selector
            instance.currentYearElement.classList.remove('flatpickr-disabled'); // Remove the disabled class
          }

          // Re-enable the month dropdown (if it exists)
          if (instance && instance.monthElements) {
            instance.monthElements.forEach((monthElement) => {
              monthElement.removeAttribute('disabled'); // Re-enable the month selector
              monthElement.classList.remove('flatpickr-disabled'); // Remove the disabled class
            });
          }

          if (selectedDates.length === 1) {
            // Disable all dates before the selected start date and restrict backward selection
            instance.set('disable', [
              function (date) {
                return date < startDate;
              },
            ]);

            // Update the minDate to prevent backward selection for end date
            instance.set('minDate', startDate);

            // Fetch unavailable dates again with the selected start date
            this.fetchUnavailableDates(
              startDate.getFullYear().toString(),
              (startDate.getMonth() + 1).toString(),
              instance,
              startDate,
            );

            // Ensure restrictions are updated
            this.updateFlatpickrDates(this.product, startDate);

            // Update price after first date if single date is selected
            if (mode === 'single') {
              this.updatePrice();
            }
          } else if (selectedDates.length === 2) {
            this.updatePrice();
          }

          if (this.startDate && this.endDate) {
            this.clearValidationError('startEndDate');
            if (this.prefilledStart) this.reconcileSelectionAfterDateChange();
            if (this.isPackage) this.loadPackageComponentDepartures();
          }
        },
        onMonthChange: (selectedDates, dateStr, instance) => {
          // Add loading class when month changes
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const startDate = selectedDates[0];
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // +1 because getMonth() is zero-indexed

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            startDate,
          );
        },
        onYearChange: (selectedDates, dateStr, instance) => {
          // Add loading class when year changes
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const startDate = selectedDates[0];
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // Adjusted to be 1-indexed

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            startDate,
          );
        },
      });
    },
    updateFlatpickrDates(product, startDate = null) {
      console.log('FUNCTION CALLED: updateFlatpickrDates');
      const instance = this.flatpickrInstances[product];

      if (instance && instance.daysContainer) {
        // Disable unavailable dates plus package-blocked start dates in pre-start mode.
        let disabledDates = [...this.unavailableDates];
        if (!startDate) {
          disabledDates = [
            ...new Set([...disabledDates, ...this.packageClosedToStartDates]),
          ];
        }

        // Set only unavailable dates initially
        instance.set('disable', disabledDates);

        // Convert categorized_dates arrays into Set for efficient lookup
        this.categorizedDates = {
          start: new Set(this.categorizedDates.start),
          single: new Set(this.categorizedDates.single),
          single2: new Set(this.categorizedDates.single2),
          middle: new Set(this.categorizedDates.middle),
          end: new Set(this.categorizedDates.end),
        };

        const unavailableDateSet = new Set(this.unavailableDates);

        const currentMonth = instance.currentMonth; // Track the currently viewed month
        const currentYear = instance.currentYear;

        const dayElements =
          instance.daysContainer.querySelectorAll('.flatpickr-day');

        dayElements.forEach((dayElem) => {
          const ariaLabel = dayElem.getAttribute('aria-label');
          const dateStr = this.convertAriaLabelToDateStr(ariaLabel); // Convert to DD-MM-YYYY format
          const dateObj = new Date(ariaLabel); // Create Date object for comparison
          const isClosedToArrival =
            this.closedToDays[dateStr]?.ct_start || false;
          const isClosedToDeparture =
            this.closedToDays[dateStr]?.ct_end || false;

          // Extract the month and year of the current date
          const elemMonth = dateObj.getMonth(); // Month is 0-indexed
          const elemYear = dateObj.getFullYear();

          // Disable dates before the startDate, if startDate is selected, but only in the same month
          if (
            startDate &&
            dateObj < startDate &&
            elemMonth === currentMonth &&
            elemYear === currentYear
          ) {
            dayElem.classList.add('flatpickr-disabled');
            dayElem.removeAttribute('tabindex'); // Prevent focusing on the disabled date
            return; // Skip further checks for this date
          }

          // Apply custom styling based on categorized dates
          if (this.categorizedDates.start.has(dateStr)) {
            dayElem.classList.add('start-date');
          }
          if (this.categorizedDates.single.has(dateStr)) {
            dayElem.classList.add('single-date');
          }
          if (this.categorizedDates.single2.has(dateStr)) {
            dayElem.classList.add('single2-date');
          }
          if (this.categorizedDates.middle.has(dateStr)) {
            dayElem.classList.add('middle-date');
          }
          if (this.categorizedDates.end.has(dateStr)) {
            dayElem.classList.add('end-date');
          }

          // Initial State: When the picker opens
          if (!this.startDate) {
            if (this.packageClosedToStartDates.includes(dateStr)) {
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault();
            }

            // Respect backend closed-to-arrival flags for package/store parity.
            if (isClosedToArrival) {
              dayElem.classList.add('has-ct-arrival');
              dayElem.classList.add('flatpickr-disabled-start');
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault();
            }

            if (
              this.categorizedDates.single.has(dateStr) ||
              this.categorizedDates.start.has(dateStr)
            ) {
              dayElem.classList.add('has-ct-arrival');
              dayElem.classList.add('flatpickr-disabled-start');
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault(); // Prevent selection on click
              // Tooltip for closed-to-arrival
              dayElem.classList.add('has-tooltip');
              dayElem._tippyClosed = tippy(dayElem, {
                content: 'Closed to Arrival',
                theme: 'light-border',
                trigger: 'mouseenter focus',
              });
            }
          } else if (this.startDate) {
            // After Start Date is Selected: Handle closed-to-arrival and closed-to-departure

            // Ensure the categorized dates (start, single, middle, end) still have their classes
            if (this.categorizedDates.start.has(dateStr)) {
              dayElem.classList.add('start-date');
            }
            if (this.categorizedDates.single.has(dateStr)) {
              dayElem.classList.add('single-date');
            }
            if (this.categorizedDates.single2.has(dateStr)) {
              dayElem.classList.add('single2-date');
            }
            if (this.categorizedDates.middle.has(dateStr)) {
              dayElem.classList.add('middle-date');
            }
            if (this.categorizedDates.end.has(dateStr)) {
              dayElem.classList.add('end-date');
            }

            // Disable previous month arrow once startDate has been selected
            if (instance && instance.prevMonthNav) {
              instance.prevMonthNav.classList.add('flatpickr-disabled'); // Add a disabled class
              instance.prevMonthNav.setAttribute('aria-disabled', 'true'); // Set accessibility attribute
              instance.prevMonthNav.onclick = (e) => e.preventDefault(); // Disable navigation
            }

            if (
              (this.categorizedDates.start.has(dateStr) ||
                this.categorizedDates.single.has(dateStr)) &&
              !unavailableDateSet.has(dateStr)
            ) {
              dayElem.classList.remove('flatpickr-disabled');
              dayElem.setAttribute('tabindex', '0');
            }

            // Respect backend closed-to-departure flags after a start date is selected.
            if (isClosedToDeparture) {
              dayElem.classList.add('has-ct-departure');
              dayElem.classList.add('flatpickr-disabled');
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault();
            }

            // Disable dates before the startDate, but only within the same month and year
            if (
              startDate &&
              dateObj < startDate &&
              elemMonth === currentMonth &&
              elemYear === currentYear
            ) {
              dayElem.classList.add('flatpickr-disabled');
              dayElem.removeAttribute('tabindex'); // Prevent focusing on the disabled date
              return; // Skip further checks for this date
            }

            // Do not infer an upper bound from categorized date classes in end-date mode.
            // Backend `unavailable_dates` is the source of truth for valid end dates.
          }
        });
      } else {
        console.error(`Flatpickr instance for '${product}' not found.`);
      }
    },
    fetchUnavailableDates(year, month, instance, startDate = null) {
      console.log('FUNCTION CALLED: fetchUnavailableDates');
      const csrfToken = getCSRFToken();

      // Add loading class to show pulsating animation
      if (instance && instance.calendarContainer) {
        instance.calendarContainer.classList.add('loading-availability');
      }

      // Check if we're using 2 months (range mode)
      // If tour duration toggle exists, use the user's choice; otherwise use RateSet's dateSelectType
      const isTwoMonths = this.showTourDurationToggle
        ? this.tourDuration !== 'single'
        : this.dateSelectType === 'range';

      let requestData = {
        year: year,
        month: month,
        soldAs: this.soldAs,
        unitId: null,
        amountUnits: this.units,
        selectedPeople: this.selectedPeople,
        tourDepartureId: this.tourDepartureId,
        two_month_mode: isTwoMonths,
        context: 'admin', // Identify this as Admin (skip min constraints)
      };

      // Add start_date_selected to requestData if startDate is provided
      if (startDate) {
        requestData.start_date_selected = formatDateISO(startDate);
      }

      // Disable date selection until availability has been fetched and updated
      this.disableFlatpickr(instance, true);

      return fetch('/bookings_app/get-unavailable-dates/', {
        method: 'POST',
        body: JSON.stringify(requestData),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          this.categorizedDates = data.categorized_dates;
          // Use backend unavailable_dates when present (contains complete package/rates constraints).
          this.unavailableDates =
            data.unavailable_dates || data.categorized_dates.middle;
          this.minMaxDays = data.min_max_days || {}; // Store min/max constraints
          this.closedToDays = data.closed_to_days || {};
          this.packageClosedToStartDates =
            data.package_closed_to_start_dates || [];
          this.packages = data.packages || []; // Store package info

          this.updateFlatpickrDates(this.product, startDate);

          // Package end-date constraints are validated in backend get-unavailable-dates.
          // Keep frontend as a pure renderer of backend unavailable_dates/closed_to_days.

          // Re-enable date selection now that availability has been updated
          this.disableFlatpickr(instance, false);

          // Remove loading class after data is loaded
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.remove('loading-availability');
          }
        })
        .catch((error) => {
          console.error('Error fetching unavailable dates:', error);

          // Remove loading class even on error
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.remove('loading-availability');
          }
        });
    },
    updatePrice() {
      console.log('FUNCTION CALLED: updatePrice');
      const csrfToken = getCSRFToken();

      // Show loading state
      this.priceLoading = true;

      if (this.soldAs && this.units) {
        const soldAsId = this.soldAs; // ID of the currently selected RateSet
        // Find the selected RateSet object from soldAsData
        const selectedRateSet = this.soldAsData.find((s) => s.id === soldAsId);
        console.log('selectedPeople:', this.selectedPeople);
        console.log('selectedAdults:', this.selectedAdults);
        console.log('selectedChildren:', this.selectedChildren);
        if (!selectedRateSet) {
          console.error('Selected RateSet not found');
          return;
        }

        let requestData;
        if (selectedRateSet.is_fixed === true) {
          console.log('FIXED INTERVALS');
          // Prepare the data to be sent
          requestData = {
            product: this.product,
            startDate: this.startDate,
            endDate: this.endDate,
            soldAs: this.soldAs,
            units: this.units,
            selectedPeople: this.selectedPeople,
          };
        } else {
          console.log('FLEXI DATES');
          // Prepare the data to be sent
          requestData = {
            product: this.product,
            startDate: this.startDate,
            soldAs: this.soldAs,
            units: this.units,
            selectedPeople: this.selectedPeople,
          };

          // Include endDate only if it's available
          if (this.endDate) {
            requestData.endDate = this.endDate;
          }
        }

        fetch('/bookings_app/calculate-price/', {
          // ADD functionality to retreive available dates from product (isavailable) and from rateset (hasrate)
          method: 'POST',
          body: JSON.stringify(requestData),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
        })
          .then(async (response) => {
            const data = await safeJsonParse(response);
            return data;
          })
          .then((data) => {
            console.log('=== DISCOUNT DEBUG (Frontend - Add Form) ===');
            console.log('Response data:', data);
            console.log('Discount amount:', data.discount_amount);
            console.log('Applied discounts:', data.applied_discounts);

            this.price = data.price; // Update the price

            // Update discount information if present
            if (data.applied_discounts && data.applied_discounts.length > 0) {
              this.discountAmount = parseFloat(data.discount_amount) || 0;
              this.appliedDiscounts = data.applied_discounts;
              console.log('Updated appliedDiscounts:', this.appliedDiscounts);
            } else {
              this.discountAmount = 0;
              this.appliedDiscounts = [];
              console.log('Reset appliedDiscounts to empty array');
            }

            // Price loaded - hide loading state
            this.priceLoading = false;
            this.triggerPriceUpdateEffect();
          })
          .catch((error) => {
            console.error('Error fetching price:', error);
            this.priceLoading = false;
          });
      } else {
        console.log('Start date or soldAs is not set');
        this.priceLoading = false;
      }
    },
    async getRateSetData(productType, productId) {
      console.log(
        'FUNCTION CALLED: getRateSetData with productType:',
        productType,
        'productId:',
        productId,
      );

      try {
        // Fetch data from the backend using the product_type and product_id
        const response = await fetch(
          `/rates/rate-sets/${productType}/${productId}/`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(), // If CSRF token is needed for GET requests
            },
          },
        );

        // Use safe JSON parsing to handle auth errors gracefully
        const data = await safeJsonParse(response);

        // Check for success and return the rate sets
        if (data.success && data.rate_sets) {
          console.log('Rate Sets Data:', data.rate_sets);
          return data.rate_sets.map((rateSet) => ({
            ...rateSet,
            id: rateSet.id.toString(), // Ensure id is string if needed
            units_count: rateSet.units_count.toString(), // Ensure units_count is string if needed
          })); // Return the rate sets
        } else {
          throw new Error(data.error || 'Error fetching rate set data');
        }
      } catch (error) {
        console.error('Error:', error);
        return []; // Handle error by returning an empty array or null
      }
    },
    async getRateSetDataOnDemand(unitId, date) {
      console.log(
        'FUNCTION CALLED: getRateSetDataOnDemand with unitId:',
        unitId,
        'date:',
        date,
      );

      try {
        // OPTIMIZED: Fetch rate data on-demand from new lightweight endpoint
        // This reduces initial page load by only fetching rate data when calendar-date is clicked
        const url = `/calendar/api/unit-rates/${unitId}/${date}/`;
        console.log('Fetching rate data from:', url);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
          console.error(
            'API request failed with status:',
            response.status,
            response.statusText,
          );
          return [];
        }

        // Use safe JSON parsing to handle auth errors gracefully
        const data = await safeJsonParse(response);
        console.log('API response data:', data);

        // Check for success and return the rate sets
        if (data.rate_sets) {
          console.log('On-Demand Rate Sets Data:', data.rate_sets);
          // Transform the data to match the expected format (endpoint now returns correct format)
          return data.rate_sets.map((rateSet) => ({
            ...rateSet,
            id: rateSet.id.toString(),
            units_count: rateSet.units_count?.toString() || '1',
          }));
        } else {
          console.warn('No rate_sets in response:', data);
          throw new Error(data.error || 'Error fetching rate set data');
        }
      } catch (error) {
        console.error('Error fetching on-demand rate data:', error);
        return []; // Handle error by returning an empty array
      }
    },
    // HELPER METHODS
    updateInitialDateForIdentifier(product, isoDate) {
      console.log('FUNCTION CALLED: updateInitialDateForIdentifier');
      if (this.flatpickrInstances[product]) {
        const newDate = parseDate(isoDate);
        if (newDate) {
          this.flatpickrInstances[product].jumpToDate(newDate, true);
        }
      }
    },
    triggerPriceUpdateEffect() {
      console.log('FUNCTION CALLED: triggerPriceUpdateEffect');
      this.priceUpdated = true;

      // Trigger CSS animation for price value
      const priceInput = document.querySelector('#price');
      if (priceInput) {
        priceInput.classList.add('price-animating');
        setTimeout(() => {
          priceInput.classList.remove('price-animating');
        }, 1800); // Match animation duration
      }

      setTimeout(() => {
        this.priceUpdated = false;
      }, 1800); // Duration of the background color effect
    },
    disableFlatpickr(flatpickrInstance, disable) {
      console.log('FUNCTION CALLED: disableFlatpickr');
      // Disable or enable the input field
      flatpickrInstance.input.disabled = disable;

      // Disable or enable the prev/next navigation buttons, if they exist
      if (flatpickrInstance.prevMonthNav && flatpickrInstance.nextMonthNav) {
        flatpickrInstance.prevMonthNav.disabled = disable;
        flatpickrInstance.nextMonthNav.disabled = disable;
      }

      // Check if calendarContainer exists
      if (flatpickrInstance.calendarContainer) {
        // Optionally, disable the year and month dropdowns if present
        const pluginElements =
          flatpickrInstance.calendarContainer.querySelectorAll(
            '.flatpickr-monthDropdown-months, .numInput',
          );
        pluginElements.forEach((element) => {
          element.disabled = disable;
        });
      }
    },
    convertDateToISO(dateStr) {
      const parts = dateStr.split('/');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    },
    convertAriaLabelToDateStr(ariaLabel) {
      const date = new Date(ariaLabel);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is zero-indexed
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    resetDatesAndPrice(clearDates = true) {
      console.log('FUNCTION CALLED: resetDatesAndPrice');

      if (clearDates) {
        // Clear dates
        this.startDate = '';
        this.endDate = '';

        // Reset fixed date range selection to the first option if needed
        this.fixedDateRange = '';
        const dropdown = document.querySelector('.fixedDateRangeDropdown');
        if (dropdown) dropdown.innerHTML = '';
      }

      this.price = 0;
      // Clear discount information
      this.discountAmount = 0;
      this.appliedDiscounts = [];
    },
    async checkDateAvailability(soldAsId, unitId) {
      console.log('Checking date availability for currently selected dates...');
      console.log('Checking with units:', this.units);

      // If no dates selected yet, return false
      if (!this.startDate || !this.endDate) {
        return false;
      }

      const csrfToken = getCSRFToken();

      // startDate and endDate are already in YYYY-MM-DD format
      const startDateISO = this.startDate;
      const endDateISO = this.endDate;
      const startDateObj = parseDate(startDateISO);
      const endDateObj = parseDate(endDateISO);
      if (!startDateObj || !endDateObj) {
        return false;
      }

      const isTwoMonths = this.showTourDurationToggle
        ? this.tourDuration !== 'single'
        : this.dateSelectType === 'range';
      // A unit id limits the check to that one unit. Amounts above 1 have to
      // count every unit on the listing, or two free units always look unavailable.
      const availabilityUnitId = Number(this.units) > 1 ? null : unitId;

      try {
        const response = await postJSONWithCSRFRetry(
          '/bookings_app/get-unavailable-dates/',
          {
            year: startDateObj.getFullYear().toString(),
            month: (startDateObj.getMonth() + 1).toString(),
            soldAs: soldAsId,
            unitId: availabilityUnitId,
            amountUnits: this.units, // Pass the units count for availability check
            selectedPeople: this.selectedPeople,
            tourDepartureId: this.tourDepartureId,
            two_month_mode: isTwoMonths,
            context: 'admin',
          },
        );

        const data = await response.json();
        console.log('Availability check response:', data);
        const unavailableDates = new Set(
          data.unavailable_dates || data.categorized_dates?.middle || [],
        );
        const packageClosedToStartDates = new Set(
          data.package_closed_to_start_dates || [],
        );
        console.log(
          'Unavailable dates / package start-closed:',
          unavailableDates.size,
          packageClosedToStartDates.size,
        );

        // Check if any date in the range is unavailable.
        // Accommodation endDate is the checkout day, so that night is not part of the stay.
        const start = new Date(startDateObj);
        const end = new Date(endDateObj);
        const nightEnd = new Date(end);
        if (this.product === 'Accommodation' && nightEnd > start) {
          nightEnd.setDate(nightEnd.getDate() - 1);
        }

        // Helper function to convert YYYY-MM-DD to DD-MM-YYYY
        const formatDateDDMMYYYY = (isoDate) => {
          const [year, month, day] = isoDate.split('-');
          return `${day}-${month}-${year}`;
        };

        const datesToCheck = [];
        const current = new Date(start);
        while (current <= nightEnd) {
          const dateStr = formatDateISO(current);
          datesToCheck.push(dateStr);
          current.setDate(current.getDate() + 1);
        }
        console.log('Dates to check (YYYY-MM-DD):', datesToCheck);

        // Check each date against unavailable dates (converting format for comparison)
        const currentCheck = new Date(start);
        let isFirstDate = true;
        while (currentCheck <= nightEnd) {
          const dateStrISO = formatDateISO(currentCheck);
          const dateStrFormatted = formatDateDDMMYYYY(dateStrISO);

          if (unavailableDates.has(dateStrFormatted)) {
            console.log(
              `❌ Date ${dateStrISO} (${dateStrFormatted}) is unavailable for ${this.units} units`,
            );
            return false;
          }
          if (isFirstDate && packageClosedToStartDates.has(dateStrFormatted)) {
            console.log(
              `❌ Start date ${dateStrISO} (${dateStrFormatted}) blocked by required package component availability`,
            );
            return false;
          }
          isFirstDate = false;
          currentCheck.setDate(currentCheck.getDate() + 1);
        }

        // Re-check selected end date using backend start-date-aware validation.
        // This catches package constraints that only become explicit after a start date is chosen.
        const startAwareResponse = await postJSONWithCSRFRetry(
          '/bookings_app/get-unavailable-dates/',
          {
            year: startDateObj.getFullYear().toString(),
            month: (startDateObj.getMonth() + 1).toString(),
            soldAs: soldAsId,
            unitId: availabilityUnitId,
            amountUnits: this.units,
            selectedPeople: this.selectedPeople,
            tourDepartureId: this.tourDepartureId,
            two_month_mode: isTwoMonths,
            context: 'admin',
            start_date_selected: startDateISO,
          },
        );
        const startAwareData = await startAwareResponse.json();
        const startAwareUnavailableDates = new Set(
          startAwareData.unavailable_dates ||
            startAwareData.categorized_dates?.middle ||
            [],
        );
        const selectedEndFormatted = formatDateDDMMYYYY(endDateISO);
        if (startAwareUnavailableDates.has(selectedEndFormatted)) {
          console.log(
            `❌ Selected end date ${endDateISO} (${selectedEndFormatted}) is unavailable for start ${startDateISO}`,
          );
          return false;
        }

        console.log(`✅ All dates are available for ${this.units} units`);
        return true;
      } catch (error) {
        console.error('Error checking date availability:', error);
        // A failed check is not proof the new amount is unavailable.
        return true;
      }
    },
    resetFlatpickrDates(soldAsId) {
      console.log('FUNCTION CALLED: resetFlatpickrDates');

      // Ensure flatpickrInstances exist before clearing them
      Object.values(this.flatpickrInstances).forEach((flatpickrInstance) => {
        if (flatpickrInstance) {
          // Check if the instance exists
          flatpickrInstance.clear(); // Clears the selected dates
          // You can also set a default date here if needed
          // flatpickrInstance.setDate("2023-01-01", true); // Example to set a specific date
        } else {
          console.error('Flatpickr instance is undefined.');
        }
      });

      console.log('Flatpickr dates reset');
    },
    formatCurrency(amount, currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount));
    },
    closeForm() {
      document.body.classList.remove('slideform-open');
      closeAllForms();
      this.hideInitializingOverlayImmediate();
      // Deactivate backdrop
      const backdrop = document.getElementById('backdrop');
      if (backdrop) {
        backdrop.style.display = 'none';
      }
      // Reset custom fields to fresh state (like a page reload)
      this.customFields = {};
      this.customFieldsPerPerson = {};
      this.pendingFormId = '';
      this.pendingSubmitAction = '';
      this.clearDateSelectionPrefill();
    },
    resetStore() {
      document.body.classList.remove('slideform-open');
      console.log('Resetting store to initial state');

      // Reset all variables to their initial values
      this.dateClicked = '';
      this.startDate = '';
      this.endDate = '';
      this.fixedDateRange = '';
      this.showFixedDateRange = false;
      this.unavailableDates = [];
      this.product = 'Accommodation';
      this.dateSelectType = 'range';
      this.productId = null;
      this.soldAs = null;
      this.soldAsType = null;
      this.soldAsData = null;
      this.unitId = null;
      this.tourDepartureId = null;
      this.tourDepartureTime = '';
      this.departureOptions = [];
      this.componentDepartureGroups = [];
      this.selectedComponentDepartures = {};
      this.componentDepartureRequestId += 1;
      this.units = 1;
      this.clearDateSelectionPrefill();

      this.peopleAsOption = false;
      this.peoplePerUnit = 0;
      this.amountPeopleOptions = [];
      this.selectedPeople = null;

      // Reset tour duration properties
      this.tourDuration = 'single';
      this.showTourDurationToggle = false;

      this.price = 0;
      this.priceUpdated = false;
      this.priceLoading = false;
      // Reset discount information
      this.discountAmount = 0;
      this.appliedDiscounts = [];
      this.categorizedDates = {
        start: [],
        single: [],
        middle: [],
        end: [],
      };

      // Reset validation errors and submit messages
      this.clearAllValidationErrors();
      this.clearSubmitMessage();

      // Reset submission state
      this.isSubmitting = false;
      this.pendingFormId = '';
      this.pendingSubmitAction = '';
      this.hideInitializingOverlayImmediate();

      // Reset custom fields
      // IMPORTANT: Set selectedRateSet to null FIRST to prevent template rendering
      // while customFieldsPerPerson is being reset
      this.selectedRateSet = null;
      this.customFields = {};
      // Simple reset - getPersonFieldsObj() will create objects on demand
      this.customFieldsPerPerson = {};
    },
    validateForm(event, action) {
      // Prevent double submissions
      if (this.isSubmitting) {
        console.log(
          'Form submission already in progress, ignoring duplicate click',
        );
        event.preventDefault();
        return;
      }

      const form = event.target.closest('form');

      if (this.selectionGate) {
        event.preventDefault();
        this.setSubmitMessage(this.selectionGateMessage, 'error');
        return;
      }

      const missingPackageTime = (this.componentDepartureGroups || []).find(
        (group) => {
          const departures = group.departures || [];
          if (!departures.length) return true;
          return !this.selectedComponentDepartures[group.id];
        },
      );
      if (missingPackageTime) {
        event.preventDefault();
        const listingName =
          missingPackageTime.tour_name ||
          missingPackageTime.listing_name ||
          'this listing';
        this.setSubmitMessage(
          (missingPackageTime.departures || []).length
            ? `Select a time for ${listingName}.`
            : `No time can accommodate the selected dates for ${listingName}.`,
          'error',
        );
        return;
      }

      // Ensure the start and end dates are defined
      if (!this.startDate || !this.endDate) {
        event.preventDefault();
        this.setValidationError(
          'startEndDate',
          'Please select a valid start and end date.',
        );
        return;
      }

      // Validate people vs units
      if (!this.validatePeopleUnits()) {
        event.preventDefault();
        this.setSubmitMessage(this.validationErrors.peopleUnits, 'error');
        return;
      }

      // Add validation for person types (ensure at least one person is selected)
      if (this.usePersonTypes) {
        const totalPeople =
          (parseInt(this.selectedAdults) || 0) +
          (parseInt(this.selectedChildren) || 0);
        if (totalPeople === 0) {
          event.preventDefault();
          this.setSubmitMessage(
            'Please select at least one adult or child.',
            'error',
          );
          return;
        }
      } else if (
        this.peopleAsOption &&
        (!this.selectedPeople || this.selectedPeople === 0)
      ) {
        event.preventDefault();
        this.setSubmitMessage('Please select the number of people.', 'error');
        return;
      }

      // Validate email format
      const emailInput = form.querySelector(
        'input[type="email"], input[name="email"]',
      );
      if (emailInput && emailInput.value) {
        if (!this.validateEmail(emailInput.value)) {
          event.preventDefault();
          this.setSubmitMessage(
            'Please enter a valid email address (e.g., name@example.com)',
            'error',
          );
          // Also focus the email field to draw attention
          emailInput.focus();
          return;
        }
      }

      if (form.checkValidity()) {
        this.submitForm(event, action);
      } else {
        form.reportValidity();
      }
    },
    submitForm(event, action) {
      console.log('FORM SUBMITTED!! Action:', action);
      console.log('usePersonTypes:', this.usePersonTypes);
      console.log('selectedAdults:', this.selectedAdults);
      console.log('selectedChildren:', this.selectedChildren);

      // Set submitting flag to prevent duplicate submissions
      this.isSubmitting = true;

      // Double-check validation (in case submitForm is called directly)
      if (this.usePersonTypes) {
        const totalPeople =
          (parseInt(this.selectedAdults) || 0) +
          (parseInt(this.selectedChildren) || 0);
        if (totalPeople === 0) {
          console.error('Form submitted with no people selected');
          this.isSubmitting = false;
          return;
        }
      }

      const calendarStore = Alpine.store('calendarStore');
      const cartStore = Alpine.store('cartStore');
      const toastStore = Alpine.store('toastStore');
      calendarStore.isLoading = true;
      this.pendingSubmitAction = action;
      let formElement = event?.target?.form || event?.currentTarget?.form;
      if (formElement) {
        const container = formElement.closest(
          '#addAccommodationForm, #addTourForm',
        );
        if (container) {
          this.pendingFormId = container.id;
        }
      } else {
        formElement = this.getActiveAddBookingFormElement();
      }
      if (!formElement) {
        console.error('Could not find form element for submission');
        calendarStore.isLoading = false;
        this.isSubmitting = false;
        return;
      }
      const formData = new FormData(formElement);
      formData.append('action', action);
      if (this.componentDepartureGroups.length) {
        formData.set(
          'component_departures',
          JSON.stringify(this.selectedComponentDepartures),
        );
      }
      if (this.guestEmailAction) {
        formData.append('guest_email_action', this.guestEmailAction);
      }
      if (
        this.pinnedUnitIds.length > 1 &&
        Number(this.units) === this.pinnedUnitIds.length
      ) {
        this.pinnedUnitIds.forEach((unitId) => {
          formData.append('selected_unit_ids', unitId);
        });
      }

      // Handle person type data based on unit type
      if (this.unitType === 'Person' && this.usePersonTypes) {
        formData.set('person_type', this.personType);
      } else if (this.usePersonTypes) {
        // Ensure adults and children are added if usePersonTypes is true
        const adults = this.selectedAdults || '0';
        const children = this.selectedChildren || '0';
        formData.set('amount_adults', adults);
        formData.set('amount_children', children);
        console.log(
          'Adding person types - adults:',
          adults,
          'children:',
          children,
        );
      }

      // Add custom field values to form data
      if (this.customFields && Object.keys(this.customFields).length > 0) {
        console.log('Adding custom fields:', this.customFields);
        Object.keys(this.customFields).forEach((fieldName) => {
          const value = this.customFields[fieldName];
          if (value !== null && value !== undefined && value !== '') {
            formData.set(`custom_field_${fieldName}`, value);
          }
        });
      }

      // Add per-person custom field values to form data
      // customFieldsPerPerson is now an object with numeric keys
      if (
        this.customFieldsPerPerson &&
        Object.keys(this.customFieldsPerPerson).length > 0
      ) {
        console.log(
          'Adding per-person custom fields:',
          this.customFieldsPerPerson,
        );
        Object.entries(this.customFieldsPerPerson).forEach(
          ([personIndex, personFields]) => {
            Object.keys(personFields).forEach((fieldName) => {
              const value = personFields[fieldName];
              if (value !== null && value !== undefined && value !== '') {
                formData.set(
                  `custom_field_${fieldName}_person_${personIndex}`,
                  value,
                );
              }
            });
          },
        );
      }

      // Also collect custom fields directly from form inputs (for adult/child fields that might not be in store)
      // This ensures fields inside Alpine.js x-if templates are included
      if (formElement) {
        const customFieldInputs = formElement.querySelectorAll(
          '[name^="custom_field_"]',
        );
        console.log(
          `Found ${customFieldInputs.length} custom field inputs in form`,
        );
        customFieldInputs.forEach((input) => {
          const name = input.name;
          let value = null;

          if (input.type === 'checkbox') {
            value = input.checked ? 'true' : '';
          } else if (input.type === 'radio') {
            if (input.checked) {
              value = input.value;
            } else {
              return; // Skip unchecked radio buttons
            }
          } else {
            value = input.value;
          }

          // Always add to FormData (will overwrite if already exists, which is fine)
          // Only skip if value is truly empty/null/undefined
          if (value !== null && value !== undefined && value !== '') {
            formData.set(name, value);
            console.log(
              `Added custom field from form input: ${name} = ${value}`,
            );
          } else {
            console.log(`Skipping empty custom field: ${name}`);
          }
        });
      } else {
        console.warn(
          'Form element not found, cannot collect custom fields from inputs',
        );
      }

      // Log all form data
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      // Set flag BEFORE fetch to prevent race condition with WebSocket message
      calendarStore.skipNextWebSocketUpdate = true;

      fetch('/bookings_app/create_booking/', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response, {
            allowErrorStatus: true,
          });
          return { status: response.status, body: data };
        })
        .then(({ status, body }) => {
          if (status === 409 && body && body.status === 'email_in_use') {
            calendarStore.isLoading = false;
            this.isSubmitting = false;
            this.openGuestEmailReuseModal(body);
            return;
          }
          if (status >= 400) throw body;
          this.guestEmailAction = '';
          this.pendingFormId = '';
          this.showGuestEmailReuseModal = false;
          document.body.classList.remove('slideform-open');
          this.closeForm();
          console.log('Booking created successfully:', body);

          // Replace all booking items belonging to new booking and add new ones
          const newItemsMap = new Map(
            body.bookingItems.map((item) => [item.item.id, item]),
          );
          // Filter out any old items that are going to be updated
          let updatedBookingItems = calendarStore.bookingItems.filter(
            (item) => !newItemsMap.has(item.item.id),
          );
          // Then, add all new items (this adds both completely new items and updates existing ones)
          updatedBookingItems = updatedBookingItems.concat(body.bookingItems);
          // Finally, assign the updated list back to the store

          // Check if blocked dates have changed before updating
          const blockedDatesChanged = !deepEqual(
            calendarStore.calendarDatesBlocked,
            body.calendar_dates_blocked,
          );

          if (blockedDatesChanged) {
            toastStore.addToast(
              'Unit blocks have been updated due to linked availability',
              'info',
            );
          }

          // Update all calendar state simultaneously to prevent flickering
          // This ensures bookingItems and calendarDatesBlocked are updated together
          calendarStore.bookingItems = updatedBookingItems;
          calendarStore.calendarDatesBlocked = body.calendar_dates_blocked;
          calendarStore.syncBookedDatesFromItems();
          // Clear filter cache to ensure new items are rendered
          calendarStore._bookingItemsCache = null;
          calendarStore._blockItemsCache = null;
          calendarStore.isLoading = false;
          cartStore.editModeBooking = body.edit_mode_booking;
          cartStore.bookingItemsCount = body.booking_items_count;
          //toastStore.addToast(body.message, 'success');

          const fields = document.getElementsByClassName('slideForm-field');
          Array.from(fields).forEach((field) => {
            field.value = '';
          });

          // Reset submitting flag after successful completion
          this.isSubmitting = false;
        })
        .catch((error) => {
          console.error('Error creating booking:', error);
          calendarStore.isLoading = false;
          // Reset submitting flag on error so user can retry
          this.isSubmitting = false;
          toastStore.addToast(
            (error && (error.message || error.error)) ||
              'An unexpected error occurred',
            'danger',
          );
        });
    },
    openGuestEmailReuseModal(body) {
      this.guestEmailReuse = {
        existing_guest_name: body.existing_guest_name || 'this guest',
        booking_count: Number(body.booking_count) || 1,
      };
      this.showGuestEmailReuseModal = true;
    },
    guestEmailReuseBookingLabel() {
      return this.guestEmailReuse.booking_count === 1 ? 'booking' : 'bookings';
    },
    getActiveAddBookingFormId() {
      if (
        this.pendingFormId === 'addAccommodationForm' ||
        this.pendingFormId === 'addTourForm'
      ) {
        return this.pendingFormId;
      }
      return this.product === 'Accommodation'
        ? 'addAccommodationForm'
        : 'addTourForm';
    },
    getActiveAddBookingFormElement() {
      const formId = this.getActiveAddBookingFormId();
      return document.querySelector(`#${formId} form`);
    },
    closeGuestEmailReuseModal() {
      this.showGuestEmailReuseModal = false;
      this.guestEmailAction = '';
    },
    confirmReuseExistingGuest() {
      this.showGuestEmailReuseModal = false;
      this.guestEmailAction = 'reuse';
      this.submitForm(
        { currentTarget: { form: null }, target: { form: null } },
        this.pendingSubmitAction || 'save',
      );
    },
    confirmKeepGuestSeparate() {
      this.showGuestEmailReuseModal = false;
      this.guestEmailAction = 'keep_separate';
      this.submitForm(
        { currentTarget: { form: null }, target: { form: null } },
        this.pendingSubmitAction || 'save',
      );
    },
    fillPinnedPersonAdults() {
      if (
        !this.usePersonTypes ||
        this.soldAsType !== 'Person' ||
        !this.pinnedUnitCount
      ) {
        return;
      }
      const count = Number(this.pinnedUnitCount);
      const cap = Math.max(
        count,
        parseInt(this.totalUnits, 10) || 0,
        parseInt(this.maxPersons, 10) || 0,
      );
      this.generatePersonTypeOptions(cap);
      this.units = count;
      this.selectedAdults = count;
      this.selectedChildren = 0;
    },

    // New method to generate options for adults and children dropdowns
    generatePersonTypeOptions(totalUnits) {
      console.log('FUNCTION CALLED: generatePersonTypeOptions');

      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.soldAsType === 'Person' ||
        this.soldAsType === 'Adult' ||
        this.soldAsType === 'Child';

      if (isPersonUnitType) {
        // For Person/Adult/Child type, use totalUnits as maxPersons
        this.maxPersons = parseInt(totalUnits) || 1;
        console.log(
          'Person unit type - maxPersons set to totalUnits:',
          this.maxPersons,
        );
      } else {
        // For other types, calculate based on units * peoplePerUnit
        if (this.peoplePerUnit == null || this.units == null) {
          console.warn(
            'peoplePerUnit or units is null, cannot generate options',
          );
          this.adultOptions = [0];
          this.childrenOptions = [0];
          return;
        }

        this.maxPersons = this.peoplePerUnit * this.units;
        console.log('Other type - maxPersons calculated:', {
          peoplePerUnit: this.peoplePerUnit,
          units: this.units,
          maxPersons: this.maxPersons,
        });
      }

      // Generate options from 0 to maxPersons (inclusive)
      this.adultOptions = [...Array(this.maxPersons + 1).keys()];
      this.childrenOptions = [...Array(this.maxPersons + 1).keys()];

      console.log('Generated options:', {
        adultOptions: this.adultOptions,
        childrenOptions: this.childrenOptions,
      });

      // Set default values
      this.selectedAdults = 0;
      this.selectedChildren = 0;
    },

    // Update options based on the other field's selection
    updatePersonTypeOptions() {
      console.log('FUNCTION CALLED: updatePersonTypeOptions');

      // Calculate remaining slots
      const remainingSlots =
        this.maxPersons -
        (parseInt(this.selectedAdults) + parseInt(this.selectedChildren));
      console.log('Remaining slots:', remainingSlots);

      // Update adults options if children changed
      if (this.selectedChildren !== null) {
        const maxAdults = this.maxPersons - parseInt(this.selectedChildren);
        this.adultOptions = [...Array(maxAdults + 1).keys()];
      }

      // Update children options if adults changed
      if (this.selectedAdults !== null) {
        const maxChildren = this.maxPersons - parseInt(this.selectedAdults);
        this.childrenOptions = [...Array(maxChildren + 1).keys()];
      }

      console.log('Updated options:', {
        adultOptions: this.adultOptions,
        childrenOptions: this.childrenOptions,
        selectedAdults: this.selectedAdults,
        selectedChildren: this.selectedChildren,
      });
    },

    // Handle changes to adult/child selections
    async handlePersonTypeChange() {
      console.log('FUNCTION CALLED: handlePersonTypeChange');
      if (this.suppressUnitDateCheck || this.isInitializing) return;
      const { start: currentStartDate, end: currentEndDate } =
        this.selectedDatesSnapshot();
      // Update both fields' options
      this.updatePersonTypeOptions();

      const totalPeople =
        parseInt(this.selectedAdults || 0) +
        parseInt(this.selectedChildren || 0);

      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.soldAsType === 'Person' ||
        this.soldAsType === 'Adult' ||
        this.soldAsType === 'Child';

      if (isPersonUnitType) {
        // Adults and children are the unit count for these listings.
        this.units = totalPeople;
        if (
          this.pinnedUnitIds.length &&
          Number(this.units) !== this.pinnedUnitIds.length
        ) {
          this.pinnedUnitIds = [];
          this.pinnedUnitCount = null;
        }
        console.log('Units updated:', {
          adults: this.selectedAdults,
          children: this.selectedChildren,
          totalUnits: this.units,
        });
        this.validatePeopleUnits();
        await this.settleDatesForUnitCount(currentStartDate, currentEndDate);
        return;
      }

      // For other unit types, update selectedPeople with the total
      this.selectedPeople = totalPeople;
      console.log('People updated:', {
        adults: this.selectedAdults,
        children: this.selectedChildren,
        totalPeople: this.selectedPeople,
      });
      if (this.showFixedDateRange === true) {
        // Populate dropdown with options
        this.populateFixedDateRangeDropdown(this.product, this.soldAs);
      }
      this.updatePrice();

      // customFieldsPerPerson is an object - getPersonFieldsObj() creates entries on demand
      // No need to pre-initialize when person count changes

      // Validate people vs units after person type change
      this.validatePeopleUnits();
    },
    // Method to handle tour duration change
    handleTourDurationChange() {
      console.log('FUNCTION CALLED: handleTourDurationChange');

      // Determine the new mode
      const newMode = this.tourDuration === 'single' ? 'single' : 'range';
      const instance = this.flatpickrInstances[this.product];
      // The radios can emit change when the form refreshes. Only a real
      // switch between 1 Day and Multi Day should clear the selection.
      if (instance && instance.config.mode === newMode) return;

      // Reset dates when switching modes
      this.startDate = '';
      this.endDate = '';
      this.price = 0;

      // Use existing method to clear flatpickr dates
      this.resetFlatpickrDates();

      // Clear the input field visually as well
      if (this.flatpickrInstances[this.product]) {
        const element = this.flatpickrInstances[this.product].element;
        element.value = '';

        // Destroy current instance
        this.flatpickrInstances[this.product].destroy();

        // Reinitialize with new mode
        this.initializeFlatpickr(this.product, element, newMode);

        // Update initial date
        this.updateInitialDateForIdentifier(this.product, this.dateClicked);
      }
    },
    clearValidationError(field) {
      this.validationErrors = {
        ...this.validationErrors,
        [field]: '',
      };
    },
  });
}
